import React, { ForwardedRef, RefObject, useCallback, FocusEvent } from 'react';
import { ComboBoxAria, useComboBox, useFilter } from 'react-aria';
import { ComboBoxState, ComboBoxStateOptions, useComboBoxState } from 'react-stately';
import { useLogger, Loggable } from '@direct-frontend/modules-logger';

import { getDefaultRenderer } from '../ListBoxBase/Render/Render';
import { ListBoxItem, ListBoxOptionItem, ListBoxSectionItem } from '../ListBox';
import { getItemKey } from '../ListBoxBase/Item/utils';
import { getDisabledKeys } from '../ListBoxBase/utils';
import { useObjectRef } from '../../hooks/useObjectRef';
import { TextInput, TextInputProps } from '../TextInput';
import { processItems } from '../ListBox/utils';

import { ComboboxProps, InputRenderer, VirtualizedComboboxProps } from './types';

export const defaultChildren: InputRenderer = props => <TextInput {...props} />;

export const nodeInfo = { name: 'Combobox' };

export const autoFocusProps = {};

export const getRenderItems = <ValueType extends string, SectionType extends string>() =>
    getDefaultRenderer<ListBoxOptionItem<ValueType>, ListBoxSectionItem<SectionType, ValueType>>(getItemKey);

const emptyFilterFn = () => true;

interface UseComboboxPropsOptions<ValueType extends string, SectionType extends string = ValueType>
    extends Omit<ComboboxProps<ValueType, SectionType>, 'menuTrigger'>,
        Pick<
            ComboBoxStateOptions<ListBoxItem<ValueType, SectionType>>,
            'menuTrigger' | 'shouldCloseOnBlur' | 'allowsEmptyCollection'
        > {}

interface ComboboxOptions<
    ValueType extends string,
    SectionType extends string,
    ItemType extends ListBoxItem<ValueType, SectionType>,
> {
    listBoxRef: RefObject<HTMLDivElement>;
    inputRef: RefObject<HTMLInputElement>;
    popoverRef: RefObject<HTMLDivElement>;
    inputProps: Omit<ComboBoxAria<ItemType>['inputProps'], 'color' | 'width'> & Pick<TextInputProps, 'color'>;
    listBoxProps: ComboBoxAria<ItemType>['listBoxProps'] & Loggable;
    state: ComboBoxState<ItemType>;
    LogProvider: ReturnType<typeof useLogger>['LogProvider'];
}

export function useComboboxProps<ValueType extends string, SectionType extends string = ValueType>(
    props: UseComboboxPropsOptions<ValueType, SectionType>,
    ref: ForwardedRef<HTMLInputElement>,
):
    | ComboboxOptions<ValueType, SectionType, ListBoxItem<ValueType, SectionType>>
    | ComboboxOptions<ValueType, SectionType, Omit<ListBoxOptionItem<ValueType>, 'isTruncated'>> {
    const {
        items,
        filter = 'contains',
        menuTrigger = 'focus',
        allowsCustomValue = true,
        className,
        onFocusChange,
        addonBeforeSection,
        containerRef,
        popupMaxHeight,
        popupMaxWidth,
        flip,
        logNode,
        ...restProps
    } = props;

    const { LogProvider } = useLogger(nodeInfo, logNode);

    const listBoxRef = React.useRef(null);
    const inputRef = useObjectRef<HTMLInputElement>(ref);
    const popoverRef = React.useRef<HTMLDivElement>(null);

    const defaultFilter = useFilter({ sensitivity: 'base' });
    const appliedFilter = (() => {
        if (filter === 'none') {
            return emptyFilterFn;
        }
        if (typeof filter === 'string') {
            return defaultFilter[filter];
        }
        return filter;
    })();

    const preparedItems = isComboboxVirtualized(props)
        ? items.map(item => ({ ...item, isTruncated: true }))
        : processItems({ items, shouldAddAddon: Boolean(addonBeforeSection) });

    const state = useComboBoxState<(typeof preparedItems)[0]>({
        ...restProps,
        defaultItems: preparedItems,
        children: getRenderItems(),
        disabledKeys: getDisabledKeys<
            ValueType,
            SectionType,
            ListBoxOptionItem<ValueType>,
            ListBoxSectionItem<SectionType, ValueType>
        >(preparedItems, getItemKey),
        defaultFilter: appliedFilter,
        allowsCustomValue,
        menuTrigger,
    });

    const blurHandler = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            // Переход к элементам попапа не должен закрывать его и снимать фокус с инпута.
            const blurIntoPopover = e.relatedTarget && popoverRef.current?.contains(e.relatedTarget);
            // При переключении окна или вкладки инпут остаётся activeElement документа без фокуса.
            const documentLostFocus =
                !e.currentTarget.ownerDocument.hasFocus() &&
                e.currentTarget.ownerDocument.activeElement === e.currentTarget;

            if (blurIntoPopover || documentLostFocus) {
                inputRef.current?.focus({
                    preventScroll: true,
                });
                state.selectionManager.setFocused(true);
                return;
            }

            props.onBlur?.();
            state.setFocused(false);
            state.selectionManager.setFocused(false);
            onFocusChange?.(false);
        },
        [onFocusChange, inputRef, props, state, popoverRef],
    );

    const focusChangeHandler = useCallback(
        (isFocused: boolean) => {
            state.selectionManager.setFocused(isFocused);
            onFocusChange?.(isFocused);
        },
        [state.selectionManager, onFocusChange],
    );

    const {
        inputProps: { color, width, ...inputProps },
        listBoxProps,
    } = useComboBox(
        {
            ...props,
            items: undefined,
            defaultItems: preparedItems,
            inputRef,
            listBoxRef,
            popoverRef,
            menuTrigger,
            onFocusChange: focusChangeHandler,
        },
        state,
    );

    return {
        listBoxRef,
        inputRef,
        popoverRef,
        state,

        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        inputProps: {
            ...inputProps,

            // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
            ...(popoverRef?.current && { onBlur: blurHandler }),
        },
        listBoxProps: { ...listBoxProps, logNode },
        LogProvider,
    };
}

export const isComboboxStateVirtualized = <ValueType extends string, SectionType extends string = ValueType>(
    props: ComboboxProps<ValueType, SectionType>,
    _state: ComboBoxState<ListBoxItem<ValueType, SectionType> | Omit<ListBoxOptionItem<ValueType>, 'isTruncated'>>,
): _state is ComboBoxState<Omit<ListBoxOptionItem<ValueType>, 'isTruncated'>> => {
    return Boolean(props.virtualized);
};

export const isComboboxVirtualized = <ValueType extends string, SectionType extends string = ValueType>(
    props: ComboboxProps<ValueType, SectionType> | UseComboboxPropsOptions<ValueType, SectionType>,
): props is VirtualizedComboboxProps<ValueType, SectionType> => {
    return Boolean(props.virtualized);
};

export const isFocusable = (element: EventTarget | null) => {
    if (!(element instanceof HTMLElement)) {
        return false;
    }

    const knownFocusableElements =
        'a,area[href],button:not([disabled]),details,iframe,object,input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[contentEditable="true"]';
    if (element.matches(knownFocusableElements)) {
        return true;
    }

    return false;
};

/** Оставляет из autoComplete только значения, которые принимает TextInput. */
export const getOnOffAutoComplete = (value: string | undefined): 'on' | 'off' | undefined =>
    value === 'on' || value === 'off' ? value : undefined;
